import React, { useCallback, useMemo } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import type { TriggerProps as PopupTriggerProps } from '@atlaskit/popup/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { TriggerProps as TooltipTriggerProps } from '@atlaskit/tooltip/types';

import { BylineConnectionsTrigger } from './BylineConnectionsTrigger.tsx';
import type { BylineConnectionIconStackProps } from './BylineConnectionIconStack.tsx';

const i18n = defineMessages({
	tooltipTwg: {
		id: 'byline-connections.tooltip-twg-v2',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "See how this content connects to your team's work",
		description:
			"Tooltip shown on hover over the connections byline item, describing that it shows how the current page connects to the team's work.",
	},
});

/**
 * Forwards a node to a React ref that may be either a callback ref or an object
 * ref. Exported for unit testing so both ref shapes are covered.
 */
export const assignRef = (
	ref: React.Ref<HTMLButtonElement>,
	node: HTMLButtonElement | null,
): void => {
	if (typeof ref === 'function') {
		ref(node);
	} else if (ref && 'current' in ref) {
		(ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
	}
};

/**
 * Resolves the byline tooltip text. Returns an empty string while the menu is
 * open (the tooltip is suppressed then), otherwise the Teamwork Graph label.
 */
const getTooltipContent = (isOpen: boolean, formatMessage: IntlShape['formatMessage']): string => {
	if (isOpen) {
		return '';
	}
	return formatMessage(i18n.tooltipTwg);
};

export type BylineConnectionsTooltipTriggerProps = Pick<
	BylineConnectionIconStackProps,
	'links' | 'incomingLinks' | 'additionalIcons' | 'minIcons'
> & {
	isOpen: boolean;
	popupTriggerProps: PopupTriggerProps;
	triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
	onToggle: () => void;
};

type TooltipTriggerButtonProps = BylineConnectionsTooltipTriggerProps & {
	tooltipTriggerProps: TooltipTriggerProps;
};

// Hooks belong to this component rather than the Tooltip render callback.
const TooltipTriggerButton = ({
	links,
	incomingLinks,
	additionalIcons,
	minIcons,
	isOpen,
	popupTriggerProps,
	triggerRef,
	onToggle,
	tooltipTriggerProps,
}: TooltipTriggerButtonProps): React.JSX.Element => {
	const popupRef = popupTriggerProps.ref;
	const tooltipRef = tooltipTriggerProps.ref;
	const setTriggerRef = useCallback(
		(node: HTMLButtonElement | null) => {
			triggerRef.current = node;
			assignRef(popupRef, node);
			tooltipRef(node);
		},
		[triggerRef, popupRef, tooltipRef],
	);
	const buttonProps = useMemo<React.ComponentProps<typeof BylineConnectionsTrigger>['buttonProps']>(
		() => ({
			...tooltipTriggerProps,
			...popupTriggerProps,
			ref: setTriggerRef,
			onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
				tooltipTriggerProps.onClick(event);
				onToggle();
			},
			'aria-expanded': isOpen,
			'aria-haspopup': 'dialog',
		}),
		[popupTriggerProps, tooltipTriggerProps, setTriggerRef, onToggle, isOpen],
	);
	return (
		<BylineConnectionsTrigger
			appearance="loaded"
			links={links}
			incomingLinks={incomingLinks}
			additionalIcons={additionalIcons}
			minIcons={minIcons}
			buttonProps={buttonProps}
		/>
	);
};

export const BylineConnectionsTooltipTrigger = (
	props: BylineConnectionsTooltipTriggerProps,
): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Tooltip content={getTooltipContent(props.isOpen, formatMessage)}>
			{(tooltipTriggerProps: TooltipTriggerProps) => (
				<TooltipTriggerButton {...props} tooltipTriggerProps={tooltipTriggerProps} />
			)}
		</Tooltip>
	);
};
